/*
 * Focus-mode search controller (Phase A). Owns the entry interaction, a fixed
 * header-overlay bar, a dim/blur backdrop, the two-pane panel (desktop) /
 * near-full-screen sheet (mobile), the progressive empty state, the live query
 * path, keyboard + a11y, and ATC coordination. Reuses the data/render layer:
 * window.AISearchClient, window.AICrossStore, window.AISearchAnalytics.
 *
 * Loaded ONLY when settings.ai_search_enabled is ON. Native predictive-search.js
 * and #Search-Drawer are NOT loaded then, so this is the sole binder of
 * .thb-quick-search. Floor: the trigger stays <a href="/search"> — if this
 * script throws before binding, the click navigates to the real search page.
 */
(function () {
  var cfg = window.AISearch || {};
  var DEBOUNCE_MS = 180, HESITATION_MS = 2000, TYPE_MS = 30;
  // Zero-result review wall: only surface reviews this fresh, and hide the wall
  // entirely below this many — better empty than stale-looking. Tunable knobs.
  var REVIEW_MAX_AGE_DAYS = 120, REVIEW_MIN_COUNT = 2;

  function rm() { try { return matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } }
  function esc(s) {
    if (window.AISearchClient && AISearchClient.escapeHtml) return AISearchClient.escapeHtml(s);
    var str = String(s == null ? '' : s);
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  function A() { return window.AISearchAnalytics || null; }
  // Cross-store confidence tier as a clean enum ('high'|'soft'). Normalized in one
  // place so a stray Worker `confidence` value can never reach a DOM attribute or
  // the CTR dataset, and so each panel's impression and click log the same literal.
  function tierOf(block, lead) { return (lead || (block && block.confidence === 'high')) ? 'high' : 'soft'; }
  function client() { return window.AISearchClient || null; }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  var FS = {
    built: false, isOpen: false,
    root: null, backdrop: null, bar: null, brand: null, input: null, closeBtn: null,
    panel: null, handle: null, rail: null, results: null, live: null,
    seq: 0, debTimer: null, hesTimer: null, typeTimer: null, closeTimer: null,
    scrollY: 0, lastData: null, lastTerm: '', options: [], activeIndex: -1,
    trigger: null, lastFocus: null,
    pendingAnswer: '', pendingAnswerTerm: '',   // answer to show above results for a tapped question
  };

  /* ---- DOM construction ---- */
  FS.build = function () {
    if (this.built) return;
    var root = document.createElement('div');
    root.id = 'ai-focus-root';
    root.setAttribute('hidden', '');
    root.innerHTML =
      '<div class="ai-focus-backdrop" aria-hidden="true"></div>' +
      '<div class="ai-focus-bar" role="search">' +
        '<span class="ai-focus-brand" aria-hidden="true"></span>' +
        '<input class="ai-focus-input" type="search" autocomplete="off" autocapitalize="off" ' +
          'spellcheck="false" enterkeyhint="search" role="combobox" aria-expanded="false" ' +
          'aria-autocomplete="list" aria-controls="ai-focus-listbox" ' +
          'placeholder="Search or ask a question…" aria-label="Search or ask a question">' +
        '<button class="ai-focus-x" type="button" aria-label="Close search">×</button>' +
      '</div>' +
      '<div class="ai-focus-panel" role="dialog" aria-modal="true" aria-label="Search">' +
        '<div class="ai-focus-handle" role="button" tabindex="0" aria-label="Close search"></div>' +
        '<div class="ai-focus-body">' +
          '<aside class="ai-focus-rail"></aside>' +
          '<section class="ai-focus-results" id="ai-focus-listbox" role="listbox" aria-label="Search results"></section>' +
        '</div>' +
      '</div>' +
      '<div class="ai-focus-live" aria-live="polite" role="status"></div>';
    document.body.appendChild(root);
    this.root = root;
    this.backdrop = root.querySelector('.ai-focus-backdrop');
    this.bar = root.querySelector('.ai-focus-bar');
    this.brand = root.querySelector('.ai-focus-brand');
    this.input = root.querySelector('.ai-focus-input');
    this.closeBtn = root.querySelector('.ai-focus-x');
    this.panel = root.querySelector('.ai-focus-panel');
    this.handle = root.querySelector('.ai-focus-handle');
    this.rail = root.querySelector('.ai-focus-rail');
    this.results = root.querySelector('.ai-focus-results');
    this.live = root.querySelector('.ai-focus-live');
    this.cloneBrand();
    this.bindRoot();
    this.built = true;
  };

  // ◈ = the existing header logo, scaled (per spec). Fall back to a glyph.
  FS.cloneBrand = function () {
    try {
      var logo = document.querySelector('#header .logoimg:not(.logoimg--light)') ||
                 document.querySelector('#header .logoimg') ||
                 document.querySelector('#header .logolink img');
      if (logo) { var c = logo.cloneNode(true); c.removeAttribute('id'); c.removeAttribute('loading'); this.brand.appendChild(c); return; }
    } catch (e) {}
    this.brand.textContent = '◈';
  };

  // Lay the fixed bar over the header NAV row — measured top + height. #header
  // sits BELOW the announcement banner (and may be sticky at 0 when scrolled),
  // so we anchor to its live rect rather than assuming top:0. Returns the rect
  // so the clip origin can be computed in the same coordinate space.
  FS.syncBarToHeader = function () {
    var top = 0, height = 64;
    try {
      var h = document.getElementById('header');
      if (h) { var r = h.getBoundingClientRect(); top = Math.round(r.top); height = Math.round(r.height) || 64; }
    } catch (e) {}
    this.root.style.setProperty('--ai-focus-bar-top', top + 'px');
    this.root.style.setProperty('--ai-focus-bar-h', height + 'px');
    return { top: top, height: height };
  };

  // Set the circular-reveal origin to the search icon's CENTER, expressed
  // relative to the bar's own box (x from bar-left=0; y from bar-top=headerTop).
  // The bar then bursts open radially out of the magnifying glass.
  FS.syncClipOrigin = function (bar) {
    var w = window.innerWidth || document.documentElement.clientWidth || 0;
    var headerTop = bar ? bar.top : 0, headerH = bar ? bar.height : 64;
    var cx, cy, ok = false;
    try {
      var t = this.trigger;
      if (t && t.getBoundingClientRect) {
        var rect = t.getBoundingClientRect();
        if (rect.width && rect.bottom > 0 && rect.top < (window.innerHeight || 9999)) {
          cx = Math.round(rect.left + rect.width / 2);
          cy = Math.round(rect.top + rect.height / 2 - headerTop);
          ok = true;
        }
      }
    } catch (e) {}
    if (!ok) { cx = Math.max(0, w - 60); cy = Math.round(headerH / 2); }  // fallback: right side, mid-bar
    this.root.style.setProperty('--ai-focus-clip-x', cx + 'px');
    this.root.style.setProperty('--ai-focus-clip-y', cy + 'px');
  };

  /* ---- open / close ---- */
  FS.openFocus = function () {
    if (this.isOpen) return;            // hard guard: never double-open
    clearTimeout(this.closeTimer);      // cancel a pending close teardown (fast close→open can't blank a fresh panel)
    this.lastFocus = (document.activeElement && document.activeElement !== document.body) ? document.activeElement : this.trigger;
    this.build();
    this.isOpen = true;
    var bar = this.syncBarToHeader();
    this.syncClipOrigin(bar);
    this.lockScroll();
    this.setInert(true);
    this.root.removeAttribute('hidden');
    if (rm()) this.root.classList.add('ai-focus-reduced');
    void this.root.offsetWidth;         // reflow so the transition runs
    this.root.classList.add('is-open');
    this.input.value = '';
    this.input.setAttribute('aria-expanded', 'true');
    this.renderEmpty();
    var self = this;
    setTimeout(function () { try { self.input.focus({ preventScroll: true }); } catch (e) {} }, rm() ? 0 : 60);
    try { if (window.dispatchCustomEvent) dispatchCustomEvent('ai-focus:open'); } catch (e) {}
    try { if (window.amplitude) amplitude.track('Focus Search Opened'); } catch (e) {}
  };

  FS.closeFocus = function () {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.clearTimers();
    this.root.classList.remove('is-open');
    this.setInert(false);
    this.unlockScroll();
    var self = this;
    var finish = function () {
      self.root.setAttribute('hidden', '');
      self.results.innerHTML = '';
      self.rail.innerHTML = '';
      self.live.textContent = '';
      self.options = []; self.activeIndex = -1;
      self.input.value = '';
      self.input.setAttribute('aria-expanded', 'false');
      self.input.removeAttribute('aria-activedescendant');
    };
    if (rm()) finish(); else this.closeTimer = setTimeout(finish, 460);  // let the reverse circle-collapse finish
    var ret = this.trigger || this.lastFocus;   // fallback when opened via "/" with no trigger in DOM
    try { if (ret) ret.focus(); } catch (e) {}
  };

  FS.lockScroll = function () {
    this.scrollY = window.scrollY || window.pageYOffset || 0;
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    var b = document.body.style;
    b.position = 'fixed'; b.top = '-' + this.scrollY + 'px';
    b.left = '0'; b.right = '0'; b.width = '100%';
    if (sbw > 0) b.paddingRight = sbw + 'px';
    document.body.classList.add('ai-focus-locked');
  };
  FS.unlockScroll = function () {
    var b = document.body.style;
    document.body.classList.remove('ai-focus-locked');
    b.position = ''; b.top = ''; b.left = ''; b.right = ''; b.width = ''; b.paddingRight = '';
    window.scrollTo(0, this.scrollY);
  };

  // Make the rest of the page inert (SR + tab can't reach it) while open.
  FS.setInert = function (on) {
    try {
      each(document.body.children, function (el) {
        if (el === FS.root) return;
        if (on) {
          if (el.hasAttribute('inert')) return;
          el.setAttribute('inert', ''); el.setAttribute('data-ai-inert', '1');
        } else if (el.getAttribute('data-ai-inert')) {
          el.removeAttribute('inert'); el.removeAttribute('data-ai-inert');
        }
      });
    } catch (e) {}
  };

  FS.clearTimers = function () { clearTimeout(this.debTimer); clearTimeout(this.hesTimer); clearTimeout(this.typeTimer); };

  /* ---- empty state: example questions lead the results pane ---- */
  // Recent-search feature removed entirely. The example questions now sit at the
  // top of the results section (not the rail) and show immediately on open — no
  // hesitation delay — since they're the primary empty-state content.
  FS.renderEmpty = function () {
    this.options = []; this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.pendingAnswer = ''; this.pendingAnswerTerm = '';   // clear any tapped-question answer
    this.rail.innerHTML = '';                 // rail is now unused (no recent)
    var questions = (cfg.questions && cfg.questions.length) ? cfg.questions : [];
    this.results.innerHTML = questions.length
      ? this.questionsHtml(questions)
      : '<div class="ai-focus-cue"><p>Start typing to search products, guides, and more.</p></div>';
    this.bindQuestionButtons();
    this.collectOptions();
  };

  // Questions are {q, a} pairs (a optional). Tolerate a bare string too. The
  // answer rides along on a data-attr so the click handler can surface it above
  // the results.
  FS.questionsHtml = function (questions) {
    if (!questions.length) return '';
    return '<div class="ai-focus-questions"><h2 class="ai-focus-questions__title">Not sure? Try asking</h2>' +
      '<ul class="ai-focus-questions__list" role="list">' +
      questions.map(function (q) {
        var text = (typeof q === 'string') ? q : (q && q.q) || '';
        var ans = (q && q.a) || '';
        if (!text) return '';
        return '<li><button type="button" class="ai-focus-question" data-ai-focus-fill="' + esc(text) + '"' +
          (ans ? ' data-ai-focus-answer="' + esc(ans) + '"' : '') + '>' + esc(text) + '</button></li>';
      }).join('') + '</ul></div>';
  };

  // Tap a question → stash its answer, typewriter the question into the input,
  // and run the query. paint() then shows the answer above the product grid.
  FS.bindQuestionButtons = function () {
    var self = this;
    each(this.results.querySelectorAll('[data-ai-focus-fill]'), function (b) {
      b.addEventListener('click', function () {
        var q = b.getAttribute('data-ai-focus-fill') || '';
        self.pendingAnswer = b.getAttribute('data-ai-focus-answer') || '';
        self.pendingAnswerTerm = q.trim();
        try { if (window.amplitude) amplitude.track('Focus Search Question Tapped', { question: q }); } catch (e) {}
        self.prefillAndRun(q);
      });
    });
  };

  // Typewriter the question into the input, then run the normal query path.
  // Reduced-motion (or mid-animation interaction) sets the value and runs at once.
  FS.prefillAndRun = function (q) {
    var self = this;
    clearTimeout(this.hesTimer);
    this.clearTyping();
    if (rm()) { this.input.value = q; this.runQuery(q); return; }
    var i = 0;
    this.input.value = '';
    (function step() {
      self.input.value = q.slice(0, i + 1);
      i++;
      if (i < q.length) self.typeTimer = setTimeout(step, TYPE_MS);
      else self.runQuery(q);
    })();
  };

  /* ---- query path ---- */
  FS.onInput = function () {
    clearTimeout(this.hesTimer); this.clearTyping();
    this.pendingAnswer = ''; this.pendingAnswerTerm = '';   // a manual keystroke drops the tapped-question answer
    var term = this.input.value.trim();
    clearTimeout(this.debTimer);
    if (!term.length) { this.renderEmpty(); return; }
    var self = this;
    this.debTimer = setTimeout(function () { self.runQuery(self.input.value); }, DEBOUNCE_MS);
  };

  FS.runQuery = function (raw) {
    var term = (raw || '').trim();
    if (term.length < 2) return;
    var c = client(); if (!c || !c.ready()) return;
    var seq = ++this.seq;            // monotonic; both async phases check it
    var self = this;
    this.showSkeleton();
    c.query(term).then(function (data) {
      if (seq !== self.seq) return;                 // stale after query()
      if (data == null) { self.renderEmpty(); return; } // fail-soft → calm empty
      self.lastData = data; self.lastTerm = term;
      self.paint(seq, term, data);
    }).catch(function () { if (seq === self.seq) self.renderEmpty(); });
  };

  FS.showSkeleton = function () {
    var tiles = '';
    for (var i = 0; i < 6; i++) tiles += '<div class="ai-focus-skel"></div>';
    this.results.innerHTML = '<div class="ai-focus-skel-grid">' + tiles + '</div>';
  };

  // Storefront-truth sold-out strip. The native cards we fetch carry the LIVE
  // out-of-stock badge straight from the theme, which is authoritative even when
  // the search index's `available` flag is stale (seen: index says in-stock, the
  // storefront card shows "Out Of Stock"). Drop any card whose rendered badge
  // says out of stock; returns the surviving markup + kept count. Matched to the
  // theme's badge label — if that label is reworded, this fails OPEN (card shows)
  // rather than hiding the wrong product.
  FS.stripSoldOut = function (html) {
    if (!html) return { html: '', kept: 0 };
    var tpl = document.createElement('template');
    tpl.innerHTML = html;
    each(tpl.content.querySelectorAll('li.column'), function (li) {
      var badges = li.querySelectorAll('.badge, .badges');
      var oos = Array.prototype.some.call(badges, function (b) { return /\bout\s+of\s+stock\b/i.test(b.textContent || ''); });
      if (oos) li.remove();
    });
    // Drop native `title` attrs — the theme card sets title="<product>" on the
    // link, which fires a cheap grey browser tooltip over the premium overlay.
    // The card keeps its visible title text + accessible name, so this is a11y-safe.
    each(tpl.content.querySelectorAll('[title]'), function (el) { el.removeAttribute('title'); });
    return { html: tpl.innerHTML, kept: tpl.content.querySelectorAll('li.column').length };
  };

  FS.paint = function (seq, term, data) {
    var c = client(), self = this;
    // Pre-filter on the payload's explicit `available === false` (cheap, saves
    // card fetches). `!== false` treats a missing flag as in-stock. The fetched
    // cards then get a second, authoritative pass in stripSoldOut() below.
    var products = (data.products || []).filter(function (p) { return p.available !== false; });
    var cardsPromise = products.length ? c.renderProductCards(products, 'focus') : Promise.resolve('');
    cardsPromise.then(function (cardsHtml) {
      if (seq !== self.seq) return;                 // stale after the card-fetch phase (the guard the spec demands)
      var stripped = self.stripSoldOut(cardsHtml);  // storefront-truth OOS removal
      var count = stripped.kept;                    // focus renders only the product grid — don't count unshown articles
      var html = '';
      // Answer line for a tapped example question — sits above the grid so the
      // question feels answered, not just searched. Guarded to this exact term.
      if (self.pendingAnswer && term === self.pendingAnswerTerm) {
        html += '<div class="ai-focus-answer"><p>' + esc(self.pendingAnswer) + '</p></div>';
      }
      if (count) {
        // Sparse modifier: 1-2 results center as fixed-width cards instead of
        // stranding a dead field to the right of a 3-col track.
        var gridMod = count === 1 ? ' ai-focus-grid--n1' : (count === 2 ? ' ai-focus-grid--n2' : '');
        html += '<div class="ai-focus-count">' + count + (count === 1 ? ' result' : ' results') + '</div>';
        html += '<ul class="products collection row small-up-2 medium-up-3 ai-search-grid ai-focus-grid' + gridMod + '" role="presentation">' + stripped.html + '</ul>';
      } else {
        html += '<div class="ai-focus-zero"></div>';   // discovery fills this
        try { if (window.amplitude) amplitude.track('Focus Search Zero Results', { query: term }); } catch (e) {}
      }
      self.results.innerHTML = html;
      if (count) {
        // Own-store grid first, then "also available at Neurogan Health".
        self.renderCrossStore(data, term, self.results);
      } else if (self.isHealthHighConfidence(data)) {
        // High-confidence Health intent: promote the handoff to the lead.
        self.renderHealthLead(seq, term, data);
      } else {
        // Zero own-store: renderZero orders lead → cross-store → suggestions.
        self.renderZero(seq, term, data);
      }
      self.collectOptions();
      self.announce(count, term, data);
      try { if (A()) A().logSearch(term, count, 'focus'); } catch (e) {}
      try { if (window.amplitude) amplitude.track('Focus Search Completed', { query: term, result_count: count, has_results: count > 0 }); } catch (e) {}
    }).catch(function () {});
  };

  // High-confidence Health intent: the Worker tags the response when a sibling
  // (Neurogan Health) block names the query in its product title (score >= 80).
  // Defensive fallback to per-block confidence so a missing top-level flag still
  // promotes. Pure; never throws.
  FS.isHealthHighConfidence = function (data) {
    if (!data) return false;
    if (data.intent === 'cross-store-high') return true;
    return Array.isArray(data.crossStore) && data.crossStore.some(function (b) { return b && b.confidence === 'high'; });
  };

  // Append cross-store handoff panels into `target`. Focus renders its OWN
  // branded markup (not AICrossStore.render — that's the shared /search + drawer
  // variant) so the copy/hierarchy read as a polished sister-store handoff. We
  // keep the data-ai-xstore-link/-item/-query hooks so ai-cross-store.js's
  // capture-phase click analytics still fire. Guarded so a throw can't abort paint.
  FS.renderCrossStore = function (data, term, target) {
    if (!Array.isArray(data.crossStore) || !data.crossStore.length || !target) return;
    var self = this;
    try {
      data.crossStore.forEach(function (block) {
        var el = self.crossStoreHandoff(block, term);
        if (el) {
          target.appendChild(el);
          // Phase 3 signal: impression fired for soft-tier cross-store panels too,
          // so the learning loop can observe which soft queries earn clicks (promote).
          try { if (A()) A().track({ event_type: 'impression', surface: 'cross-store', item_kind: tierOf(block, false), query: term }); } catch (e2) {}
        }
      });
    } catch (e) {}
  };

  // Build one focus-native cross-store handoff section. Links are plain <a> to
  // the sister store (no add-to-cart — a cross-store item can't enter this cart).
  // `lead` = the elevated high-confidence variant (image-top cards + Forest CTA).
  FS.crossStoreHandoff = function (block, term, lead) {
    var products = (block.products || []).slice(0, 3);
    if (!products.length) return null;
    var c = client();
    var store = block.storeName || 'Neurogan Health';
    var qcap = term ? (term.charAt(0).toUpperCase() + term.slice(1)) : '';
    var okHost = function (u) { return /^https:\/\/(www\.)?neuroganhealth\.com(\/|$)/i.test(u || ''); };
    var href = function (u) { return okHost(u) ? u : '#'; };
    var tier = tierOf(block, lead);
    var cards = products.map(function (p) {
      var well = p.image
        ? '<span class="ai-focus-xstore__well"><img class="ai-focus-xstore__img" src="' + esc(p.image) + '" alt="" loading="lazy"></span>'
        : '<span class="ai-focus-xstore__well ai-focus-xstore__well--ph"></span>';
      var priceStr = lead
        ? (c && p.price != null && p.price !== '' ? c.formatPrice(p.price) : (p.price != null && p.price !== '' ? '$' + String(p.price) : ''))
        : (p.price != null && p.price !== '' ? '$' + String(p.price) : '');
      var price = priceStr ? '<span class="ai-focus-xstore__price">' + esc(priceStr) + '</span>' : '';
      return '<a class="ai-focus-xstore__card" href="' + esc(href(p.url)) + '" rel="noopener"' +
        ' data-ai-xstore-link="1" data-ai-item="' + esc(p.slug || '') + '" data-ai-query="' + esc(term) + '"' +
        ' data-ai-xstore-tier="' + tier + '">' +
        well +
        '<span class="ai-focus-xstore__name">' + esc(p.title) + '</span>' +
        '<span class="ai-focus-xstore__metarow">' + price +
          '<span class="ai-focus-xstore__view">View on Health<span aria-hidden="true"> &rarr;</span></span>' +
        '</span></a>';
    }).join('');
    var footer = lead
      ? '<a class="ai-focus-xstore__cta" href="' + esc(href(block.url)) + '" rel="noopener"' +
          ' data-ai-xstore-link="1" data-ai-item="__all__" data-ai-query="' + esc(term) + '"' +
          ' data-ai-xstore-tier="' + tier + '">' +
          'Shop ' + esc(store) + '<span aria-hidden="true"> &rarr;</span></a>'
      : '<a class="ai-focus-xstore__all" href="' + esc(href(block.url)) + '" rel="noopener"' +
          ' data-ai-xstore-link="1" data-ai-item="__all__" data-ai-query="' + esc(term) + '"' +
          ' data-ai-xstore-tier="' + tier + '">' +
          'Shop all on ' + esc(store) + '<span aria-hidden="true"> &rarr;</span></a>';
    var wrap = document.createElement('section');
    wrap.className = 'ai-focus-xstore' + (lead ? ' ai-focus-xstore--lead' : '');
    wrap.setAttribute('data-ai-xstore', block.tenant || '');
    wrap.setAttribute('data-ai-xstore-tier', tier);
    wrap.setAttribute('aria-label', store);
    // Lead uses the approved hero copy ("…lives in our wellness store."); the
    // non-lead (soft) path keeps today's wording verbatim so it stays byte-identical.
    var subText = lead ? ' products are available in our wellness store.' : ' products live in our wellness store.';
    wrap.innerHTML =
      '<div class="ai-focus-xstore__head">' +
        '<span class="ai-focus-xstore__pill">' + esc(store) + '</span>' +
        '<h3 class="ai-focus-xstore__title">Found on ' + esc(store) + '</h3>' +
        (qcap ? '<p class="ai-focus-xstore__sub">' + esc(qcap) + subText + '</p>' : '') +
      '</div>' +
      '<div class="ai-focus-xstore__grid">' + cards + '</div>' +
      footer;
    return wrap;
  };

  // High-confidence Health intent (zero own-store): lead with the elevated
  // handoff (no "No exact match" preamble), then a demoted "also view" CBD strip
  // built directly from data.suggestions (compact rows, no add-to-cart — the CBD
  // is intentionally secondary here). Fully synchronous (no card fetch). Fail-soft
  // → the standard renderZero experience.
  FS.renderHealthLead = function (seq, term, data) {
    var self = this, c = client();
    var slot = self.results.querySelector('.ai-focus-zero');
    if (!slot) return;
    try {
      slot.innerHTML = '';
      // 1. Elevated hero(es) first.
      var heroCount = 0;
      (data.crossStore || []).forEach(function (block) {
        var el = self.crossStoreHandoff(block, term, true);
        if (el) {
          slot.appendChild(el);
          heroCount++;
          // Phase 3 signal: impression fired once per rendered Health hero block.
          try { if (A()) A().track({ event_type: 'impression', surface: 'cross-store', item_kind: tierOf(block, true), query: term }); } catch (e2) {}
        }
      });
      if (!heroCount) { self.renderZero(seq, term, data); return; }   // nothing to promote — fall back
      // 2. Demoted CBD strip — compact rows from suggestions, only if present.
      var items = (data.suggestions || []).filter(function (p) { return p.available !== false; }).slice(0, 6);
      if (items.length) {
        var rows = items.map(function (p) {
          var thumb = p.image
            ? '<span class="ai-focus-health-also__thumb"><img src="' + esc(p.image) + '" alt="" loading="lazy"></span>'
            : '<span class="ai-focus-health-also__thumb"></span>';
          var priceStr = (c && p.price != null && p.price !== '') ? c.formatPrice(p.price) : (p.price != null && p.price !== '' ? '$' + String(p.price) : '');
          var price = priceStr ? '<span class="ai-focus-health-also__price">' + esc(priceStr) + '</span>' : '';
          var url = p.url || ('/products/' + (p.slug || ''));
          return '<a class="ai-focus-health-also__row" href="' + esc(url) + '"' +
            ' data-ai-result data-ai-item="' + esc(p.slug || '') + '" data-ai-kind="product" data-ai-surface="focus">' +
            thumb + '<span class="ai-focus-health-also__name">' + esc(p.title) + '</span>' + price + '</a>';
        }).join('');
        var demote = document.createElement('div');
        demote.className = 'ai-focus-health-also';
        demote.innerHTML =
          '<h3 class="ai-focus-health-also__title">CBD products shoppers also view on Neurogan</h3>' +
          '<div class="ai-focus-health-also__grid">' + rows + '</div>';
        slot.appendChild(demote);
      }
      self.collectOptions();
    } catch (e) {
      self.renderZero(seq, term, data);
    }
  };

  // Zero own-store results. Order: lead → Neurogan Health (relevant sister-store
  // products, the hero) → own-store suggestions + chips. We render the lead
  // ourselves and pass query=''/reviews=[] to renderDiscovery (so it emits only
  // suggestions + chips) to control the interleaving.
  // The "Loved by shoppers" review wall is appended last, async, by
  // renderZeroReviews() — see that method for the relevance / in-stock / mixing
  // rules. It runs ONLY here (zero-result state), never on the results grid.
  FS.renderZero = function (seq, term, data) {
    var c = client(), self = this;
    // Same in-stock rule as paint(): don't surface sold-out items in the
    // "often choose" suggestions either (a zero-result shopper bouncing into an
    // out-of-stock recommendation is the worst path).
    var items = (data.suggestions || []).filter(function (p) { return p.available !== false; });
    // recent='[]' — the recent-search feature is removed, so discovery emits only
    // suggestions + trending chips.
    c.renderDiscovery('', [], [], items, [], null).then(function (dhtml) {
      if (seq !== self.seq) return;
      // slot is the placeholder paint() left in results; it survives because
      // collectOptions()/announce() must never rewrite results.innerHTML.
      var slot = self.results.querySelector('.ai-focus-zero');
      if (!slot) return;
      var lead = '<p class="ai-discovery__lead">No exact match for &ldquo;' + c.escapeHtml(term) + '&rdquo;.' +
        (data.didYouMean
          ? ' Did you mean <a class="ai-didyoumean__link" href="/search?q=' + encodeURIComponent(data.didYouMean) +
            '"><strong>' + c.escapeHtml(data.didYouMean) + '</strong></a>?'
          : '') + '</p>';
      slot.innerHTML =
        '<div class="ai-discovery">' + lead + '</div>' +
        '<div class="ai-focus-zero__xstore"></div>' +
        self.stripSoldOut(dhtml).html;   // storefront-truth pass: drop sold-out suggestion cards too
      // Cross-store (relevant sister-store products) — high, right under the lead.
      var xs = slot.querySelector('.ai-focus-zero__xstore');
      if (xs) self.renderCrossStore(data, term, xs);
      self.collectOptions();
      self.renderZeroReviews(seq, term, slot);   // "Loved by shoppers" — appended last
    }).catch(function () {});
  };

  // Review wall for the ZERO-RESULT state only. Merchant rules:
  //  (1) relevant   — q-matched to the search term;
  //  (2) in-stock   — never a sold-out OR discontinued product. Neither the search
  //      index nor /products/<h>.js is reliable here (a continue-policy item reads
  //      "available" while its storefront card shows "Out Of Stock"), so each
  //      candidate is gated on its LIVE native card's badge (cardInStock), the
  //      same truth stripSoldOut() uses for the grid; a 404 = discontinued;
  //  (3) mixed      — one review per product, so it's never four of the same item.
  // Async + appended last, so it never blocks or reorders the rest of the state.
  FS.renderZeroReviews = function (seq, term, slot) {
    var c = client(), self = this;
    if (!c || !c.reviewsHighlights || !slot) return;
    c.reviewsHighlights(20, [], term).then(function (reviews) {
      if (seq !== self.seq || !reviews || !reviews.length) return;
      // Strong sentiment only: 5-star, real sentences (not emoji-only, not just an
      // echo of the product name), and dated so we can prove recency.
      var maxAgeMs = REVIEW_MAX_AGE_DAYS * 86400000;
      var strong = reviews.filter(function (r) {
        if ((Number(r.rating) || 0) < 4) return false;
        if (!r.publishedAt) return false;
        if ((Date.now() - new Date(r.publishedAt).getTime()) > maxAgeMs) return false;  // recent only
        var body = (r.body || '').replace(/%[0-9A-Fa-f]{2}/g, ' ').trim();   // rough decode for the length/word test
        if (body.length < 20) return false;
        if (!/[A-Za-z]{3,}/.test(body)) return false;                        // has actual words
        var pt = (r.product && r.product.title || '').trim().toLowerCase();
        return body.toLowerCase() !== pt;                                    // not just the product name
      });
      // Newest first — so the wall reads current, not back-filled.
      strong.sort(function (a, b) { return new Date(b.publishedAt) - new Date(a.publishedAt); });
      var seen = {}, distinct = [];
      strong.forEach(function (r) {                  // one per product → mixed
        var h = r && r.product && r.product.handle;
        if (!h || seen[h]) return;
        seen[h] = true; distinct.push(r);
      });
      distinct = distinct.slice(0, 6);               // cap the availability fetches
      Promise.all(distinct.map(function (r) {
        return self.cardInStock(r.product.handle).then(function (ok) { return ok ? r : null; });
      })).then(function (checked) {
        if (seq !== self.seq) return;                // a newer query won — drop these
        if (slot.querySelector('.ai-focus-zero__reviews')) return;  // never double-append
        var inStock = checked.filter(Boolean).slice(0, 4);
        if (inStock.length < REVIEW_MIN_COUNT) return;   // better no wall than a thin/stale-looking one
        var html = c.renderReviews(inStock, 'focus', false);
        if (!html) return;
        var wrap = document.createElement('div');
        wrap.className = 'ai-discovery__group ai-focus-zero__reviews';
        wrap.innerHTML = html;
        each(wrap.querySelectorAll('[title]'), function (el) { el.removeAttribute('title'); });  // no native tooltips
        slot.appendChild(wrap);
        self.collectOptions();                       // review links join the listbox options
      }).catch(function () {});
    }).catch(function () {});
  };

  // Live storefront in-stock check: fetch the product's native card; the "Out Of
  // Stock" badge — or a failed/404 fetch (unpublished/discontinued) — means
  // not-in-stock. Same signal stripSoldOut() trusts for the results grid.
  FS.cardInStock = function (handle) {
    if (!handle) return Promise.resolve(false);
    return fetch('/products/' + encodeURIComponent(handle) + '?view=card')
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) { return !!html && !/\bout\s+of\s+stock\b/i.test(html); })
      .catch(function () { return false; });
  };

  FS.clearTyping = function () { clearTimeout(this.typeTimer); };

  /* ---- keyboard & a11y ---- */
  // Gather every focusable result/question as a listbox option with an id. Both
  // results and the example questions now live inside #ai-focus-listbox, so
  // aria-activedescendant is valid without any aria-owns bridge.
  FS.collectOptions = function () {
    var nodes = this.results.querySelectorAll('[data-ai-result], .ai-focus-question');
    this.options = Array.prototype.slice.call(nodes);
    this.options.forEach(function (el, i) {
      el.setAttribute('role', 'option');
      if (!el.id) el.id = 'ai-focus-opt-' + i;
      el.setAttribute('aria-selected', 'false');
      el.classList.remove('ai-focus-active');
    });
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
  };

  // Apply the highlight to a specific option index (or clear it at -1).
  FS.setActiveIndex = function (i) {
    if (!this.options.length) return;
    var prev = this.options[this.activeIndex];
    if (prev) { prev.setAttribute('aria-selected', 'false'); prev.classList.remove('ai-focus-active'); }
    this.activeIndex = i;
    var el = this.options[i];
    if (!el) { this.input.removeAttribute('aria-activedescendant'); return; }
    el.setAttribute('aria-selected', 'true');
    el.classList.add('ai-focus-active');
    this.input.setAttribute('aria-activedescendant', el.id);
    try { el.scrollIntoView({ block: 'nearest' }); } catch (e) {}
  };

  // Linear wrap-around step (kept for any non-grid callers).
  FS.moveHighlight = function (dir) {
    if (!this.options.length) return;
    this.setActiveIndex((this.activeIndex + dir + this.options.length) % this.options.length);
  };

  // Geometric 2-D move: pick the nearest option in the pressed direction using
  // rendered centers, so ↓/↑ step row-to-row and ←/→ step within a row even
  // though the options are a flat list across a mixed grid (cards, chips,
  // review links) with varying column counts. Cross-axis offset is weighted so
  // the move prefers the same column (↑/↓) or same row (←/→).
  FS.moveGrid = function (dir) {
    if (!this.options.length) return;
    if (this.activeIndex < 0) { this.setActiveIndex(0); return; }
    var cur = this.options[this.activeIndex].getBoundingClientRect();
    var ccx = cur.left + cur.width / 2, ccy = cur.top + cur.height / 2;
    var best = -1, bestScore = Infinity;
    this.options.forEach(function (el, i) {
      var r = el.getBoundingClientRect();
      var dx = (r.left + r.width / 2) - ccx, dy = (r.top + r.height / 2) - ccy;
      var primary, secondary;
      if (dir === 'down') { if (dy <= 2) return; primary = dy; secondary = Math.abs(dx); }
      else if (dir === 'up') { if (dy >= -2) return; primary = -dy; secondary = Math.abs(dx); }
      else if (dir === 'right') { if (dx <= 2) return; primary = dx; secondary = Math.abs(dy); }
      else { if (dx >= -2) return; primary = -dx; secondary = Math.abs(dy); }
      var score = primary + secondary * 2;            // favor same column (↑/↓) / row (←/→)
      if (score < bestScore) { bestScore = score; best = i; }
    });
    if (best >= 0) this.setActiveIndex(best);
  };

  FS.onKeydown = function (e) {
    if (e.key === 'Escape') { e.preventDefault(); this.closeFocus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); this.moveGrid('down'); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); this.moveGrid('up'); return; }
    // Left/Right navigate the grid only once an option is highlighted; before
    // that they stay as normal text-cursor movement in the input.
    if (e.key === 'ArrowRight') { if (this.activeIndex >= 0) { e.preventDefault(); this.moveGrid('right'); } return; }
    if (e.key === 'ArrowLeft') { if (this.activeIndex >= 0) { e.preventDefault(); this.moveGrid('left'); } return; }
    if (e.key === 'Enter') { this.onEnter(e); return; }
    if (e.key === 'Tab') { this.trapTab(e); return; }
  };

  // Enter ladder: strong bestMatch PDP → highlighted option → /search?q=
  FS.onEnter = function (e) {
    var term = this.input.value.trim();
    var data = this.lastData;
    if (data && data.bestMatch && this.lastTerm === term) {
      var bm = null;
      (data.products || []).forEach(function (p) { if (p.slug === data.bestMatch) bm = p; });
      if (bm && bm.url) {
        e.preventDefault();
        try { if (A()) A().logClick(bm.slug, 'product', 1, 'focus'); } catch (er) {}
        window.location.href = bm.url; return;
      }
    }
    if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
      e.preventDefault();
      var el = this.options[this.activeIndex];
      if (el.getAttribute('data-ai-focus-fill')) { this.prefillAndRun(el.getAttribute('data-ai-focus-fill')); return; }
      var link = (el.tagName === 'A') ? el : el.querySelector('a[href]');
      if (link && link.href) { window.location.href = link.href; return; }
      el.click(); return;
    }
    if (term.length) { e.preventDefault(); window.location.href = '/search?q=' + encodeURIComponent(term); }
  };

  // Trap Tab within the root so SR/keyboard can't reach the inert page.
  FS.trapTab = function (e) {
    var sel = 'a[href],button:not([disabled]),input,[tabindex]:not([tabindex="-1"])';
    var f = Array.prototype.filter.call(this.root.querySelectorAll(sel), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  FS.announce = function (productCount, term, data) {
    if (!this.live) return;
    var msg;
    if (productCount > 0) msg = productCount + (productCount === 1 ? ' result' : ' results');
    else if (data && data.crossStore && data.crossStore.length) msg = 'No products — showing related stores';
    else msg = 'No results for ' + term;
    this.live.textContent = msg;
  };

  /* ---- root bindings ---- */
  FS.bindRoot = function () {
    var self = this;
    this.input.addEventListener('input', function () { self.onInput(); });
    this.input.addEventListener('keydown', function (e) { self.onKeydown(e); });
    this.closeBtn.addEventListener('click', function () { self.closeFocus(); });
    this.handle.addEventListener('click', function () { self.closeFocus(); });
    this.handle.addEventListener('keydown', function (e) { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); self.closeFocus(); } });
    this.backdrop.addEventListener('click', function () { self.closeFocus(); });
    this.bindSwipe();
    this.bindViewport();
    // ATC inside panel: yield to ai-search.js's document capture handler (does the fetch + opens drawer),
    // then close focus so the cart drawer animates in over a clean page.
    this.root.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-ai-atc]') : null;
      if (!btn) return;
      try { if (window.amplitude) amplitude.track('Focus Search ATC Clicked', { query: self.lastTerm, item: btn.getAttribute('data-product-handle') || '' }); } catch (e2) {}
      setTimeout(function () { self.closeFocus(); }, 0);
    }, true);
    // Result clicks — fire with search context (query, item, surface).
    this.root.addEventListener('click', function (e) {
      var result = e.target && e.target.closest ? e.target.closest('[data-ai-result]') : null;
      if (!result) return;
      try { if (window.amplitude) amplitude.track('Focus Search Result Clicked', { query: self.lastTerm, item: result.getAttribute('data-ai-item') || '', kind: result.getAttribute('data-ai-kind') || '', surface: result.getAttribute('data-ai-surface') || 'focus' }); } catch (e2) {}
    });
  };

  // Swipe-down on the sheet (mobile) closes — only when results are scrolled to top.
  FS.bindSwipe = function () {
    var self = this, y0 = null;
    this.panel.addEventListener('touchstart', function (e) {
      if (self.results.scrollTop > 0) { y0 = null; return; }
      y0 = e.touches[0].clientY;
    }, { passive: true });
    this.panel.addEventListener('touchmove', function (e) {
      if (y0 == null) return;
      if (e.touches[0].clientY - y0 > 70) { y0 = null; self.closeFocus(); }
    }, { passive: true });
  };

  // Mobile keyboard: track visualViewport so the sheet height = visible band.
  FS.bindViewport = function () {
    if (!window.visualViewport) return;
    var self = this;
    var apply = function () { try { self.root.style.setProperty('--ai-focus-vh', window.visualViewport.height + 'px'); } catch (e) {} };
    window.visualViewport.addEventListener('resize', apply);
    window.visualViewport.addEventListener('scroll', apply);
    apply();
  };

  /* ---- trigger + global ---- */
  FS.bindTrigger = function () {
    var self = this;
    var triggers = document.querySelectorAll('.thb-quick-search');
    each(triggers, function (t) {
      if (!self.trigger) self.trigger = t;
      t.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); self.openFocus(); }, true);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      var t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      e.preventDefault(); self.openFocus();
    });
  };

  function init() {
    try { FS.bindTrigger(); window.AIFocusSearch = FS; }
    catch (e) { /* init-failure floor: the <a href="/search"> trigger still works */ }
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
